import { ArtifactKind } from './iConfig.js';

/**
 * Purpose:
 * Single source of truth for which pre-built artifact kinds each engine consumes in client/OCI mode.
 * Mirrors csharp Assembler/Config/EngineArtifactProfile.cs.
 *
 * Responsibilities:
 * - Map an engine name (Normal, PreProcess, NormalJson, PreProcessJson) to the ArtifactKind set its
 *   loader rehydrates from, so artifact GENERATION can be engine-typed (emit only what that engine needs).
 * - Encode the rule that a production deployment ships ONE engine, so a per-engine artifact set is the
 *   natural unit — and a PreProcess set carrying no raw Html forces the loader to rehydrate Preprocessed
 *   instead of silently re-parsing (which would make PreProcess degenerate into Normal).
 */
export class EngineArtifactProfile {
    // Engine -> the artifact kinds its loader consumes. DataJson is the shared content payload every
    // engine needs; the differentiator is the template-shape kind (Html / HtmlJson / Preprocessed / Metadata).
    private static readonly _required = new Map<string, string[]>([
        ['normal', [ArtifactKind.Html, ArtifactKind.DataJson]],
        ['preprocess', [ArtifactKind.Preprocessed, ArtifactKind.DataJson]],
        ['normaljson', [ArtifactKind.HtmlJson, ArtifactKind.DataJson]],
        ['preprocessjson', [ArtifactKind.Metadata, ArtifactKind.HtmlJson, ArtifactKind.DataJson]],
    ]);

    /**
     * The artifact kinds the named engine requires. Unknown/empty engine returns the union of all kinds
     * (null engineType = "generate everything", preserving the pre-engine-typed default).
     */
    static requiredKinds(engineType: string | null | undefined): Set<string> {
        if (engineType) {
            const kinds = EngineArtifactProfile._required.get(engineType.toLowerCase());
            if (kinds) return new Set<string>(kinds);
        }
        // Default / unknown: the full set so callers that don't engine-type keep emitting everything.
        return new Set<string>([
            ArtifactKind.Html, ArtifactKind.DataJson, ArtifactKind.HtmlJson,
            ArtifactKind.Preprocessed, ArtifactKind.Metadata,
        ]);
    }

    /** True when the engine (or null/unknown default) consumes the given artifact kind. */
    static requires(engineType: string | null | undefined, kind: string): boolean {
        return EngineArtifactProfile.requiredKinds(engineType).has(kind);
    }
}
